import { useEffect, useRef, useState } from 'react';
import { Wrench, HardDrive, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider.js';
import { flushEc2Instance, getEc2FlushStatus } from '../lib/callables.js';
import { Ec2HealthBadge } from '../components/Ec2HealthBadge.js';
import type { Ec2FlushResult, Ec2FlushState } from '@atlas/shared';

const POLL_INTERVAL_MS = 5_000;

function formatGiB(bytes: number): string {
  return `${(bytes / 1024 ** 3).toFixed(1)} GiB`;
}

function callableMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  return 'Flush failed.';
}

export function ToolsPage() {
  const { userRecord } = useAuth();
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState<Ec2FlushState['status'] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Ec2FlushResult | null>(null);
  // Stops the status polling once the page is left.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  if (!userRecord?.isAdmin) {
    return (
      <div className="p-8 text-sm text-[#73726e]">You don't have admin access.</div>
    );
  }

  // The flush outlasts one API call: start it, then poll its state until it
  // completes or fails.
  async function handleFlush() {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      let state: Ec2FlushState | null = (await flushEc2Instance()).data;
      while (
        mounted.current &&
        state &&
        (state.status === 'starting' || state.status === 'running')
      ) {
        setPhase(state.status);
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        if (!mounted.current) return;
        state = (await getEc2FlushStatus()).data;
      }
      if (!mounted.current) return;
      if (state?.status === 'complete' && state.result) {
        setResult(state.result);
        setConfirming(false);
      } else {
        setError(state?.error ?? 'Flush failed.');
      }
    } catch (err) {
      if (mounted.current) setError(callableMessage(err));
    } finally {
      if (mounted.current) {
        setRunning(false);
        setPhase(null);
      }
    }
  }

  const freed =
    result != null ? Math.max(0, result.freeBytesAfter - result.freeBytesBefore) : 0;

  return (
    <div className="max-w-2xl mx-auto px-8 py-8">
      <div className="flex items-center justify-between gap-4 mb-1">
        <div className="flex items-center gap-2">
          <Wrench size={16} className="text-[#73726e]" />
          <h1 className="text-lg font-semibold text-[#37352f]">Tools</h1>
        </div>
        <Ec2HealthBadge />
      </div>
      <p className="text-sm text-[#73726e] mb-6">
        Admin utilities for the migration box. These do not change data in S3.
      </p>

      <div className="border border-[#e9e9e7] rounded-lg p-5">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded bg-[#f1f1ef] flex items-center justify-center shrink-0 mt-0.5">
            <HardDrive size={15} className="text-[#73726e]" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold text-[#37352f]">Flush EC2 instance</h2>
            <p className="text-xs text-[#73726e] mt-1">
              Deletes local working files on the decrypt box (
              <span className="font-mono">C:\work</span> and{' '}
              <span className="font-mono">C:\migration-data</span>) and leftover SQL
              databases. Source files in S3 are not touched. Blocked while a decrypt
              is running.
            </p>

            {error && (
              <p className="flex items-start gap-1.5 text-xs text-[#e03e3e] bg-[#fbe8e8] border border-[#f5c6c6] rounded px-3 py-2 mt-3">
                <AlertCircle size={13} className="shrink-0 mt-0.5" />
                {error}
              </p>
            )}

            {result && (
              <p className="flex items-start gap-1.5 text-xs text-[#0f7b6c] mt-3">
                <CheckCircle2 size={13} className="shrink-0 mt-0.5" />
                <span>
                  Flushed {result.instanceId}. Reclaimed {formatGiB(freed)}; C: now has{' '}
                  {formatGiB(result.freeBytesAfter)} free
                  {result.sqlDropped.length > 0
                    ? `. Dropped SQL databases: ${result.sqlDropped.join(', ')}`
                    : ''}
                  .
                </span>
              </p>
            )}

            <div className="mt-4 flex items-center gap-2">
              {!confirming ? (
                <button
                  type="button"
                  onClick={() => {
                    setConfirming(true);
                    setError(null);
                  }}
                  disabled={running}
                  className="px-3 py-1.5 text-sm font-medium text-[#37352f] border border-[#e9e9e7] rounded hover:bg-[#f1f1ef] disabled:opacity-50 transition-colors"
                >
                  Flush instance
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setConfirming(false)}
                    disabled={running}
                    className="px-3 py-1.5 text-sm text-[#73726e] hover:text-[#37352f] disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleFlush()}
                    disabled={running}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-[#e03e3e] rounded hover:bg-[#c93232] disabled:opacity-50 transition-colors"
                  >
                    {running && <Loader2 size={13} className="animate-spin" />}
                    {running
                      ? phase === 'starting'
                        ? 'Starting the instance…'
                        : 'Flushing…'
                      : 'Confirm flush'}
                  </button>
                </>
              )}
            </div>
            {running && (
              <p className="text-[11px] text-[#aeaaa4] mt-2">
                {phase === 'starting'
                  ? 'The instance was stopped; booting it takes a few minutes.'
                  : 'This can take a few minutes if the disk is full.'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
